import React, { useEffect, useState } from 'react';
import { MdClose, MdDeveloperBoard, MdHistory, MdTimeline, MdUsb } from 'react-icons/md';
import { BOARD_CARDS, CHART_METRICS, PROBE_FACTS, StatusPill, duration, fetchJson, format, groupByCategory, relativeTime, tone } from './deviceHealth';
import { InstalledHardware } from './HardwareCatalog';

const RANGES = [[6, '6 h'], [24, '24 h'], [168, '7 d'], [720, '30 d']];

function Facts({ rows }) {
  if (!rows.length) return null;
  return <dl>{rows.map(([label, value]) => <div className="dm-row" key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>;
}

function ComponentRow({ title, component }) {
  return <div className="dm-component">
    <div className="dm-component-top"><span>{title}</span><StatusPill status={component.status}>{component.label}</StatusPill></div>
    <p>{component.detail}</p>
  </div>;
}

function BoardCard({ card, data }) {
  const component = data.components[card.component];
  if (!component) return null;
  const Icon = card.icon;
  return <article className="dm-card">
    <h3><Icon aria-hidden="true" size={18} />{card.title}</h3>
    <ComponentRow title={card.title} component={component} />
    <Facts rows={card.facts(data.report)} />
  </article>;
}

function PartBlock({ part, data }) {
  const measured = data.report.parts?.[part.key] || {};
  const checks = Object.entries(data.components).filter(([, c]) => c.part === part.key);
  const facts = !measured.probe_error && PROBE_FACTS[part.probe] ? PROBE_FACTS[part.probe](measured, data.report) : [];
  const rails = part.probe === 'pi5_power' ? Object.entries(measured.rails || {}) : [];
  return <div className="dm-part">
    <p className="dm-model">{part.label}</p>
    {checks.map(([key, c]) => <ComponentRow key={key} title={checks.length > 1 ? c.name.split(' · ').pop() : 'Status'} component={c} />)}
    <Facts rows={facts} />
    {rails.length > 0 && <details className="dm-details"><summary>PMIC rails</summary>
      <table className="dm-mini"><thead><tr><th>Rail</th><th>Voltage</th><th>Current</th></tr></thead>
        <tbody>{rails.sort().map(([name, rail]) => <tr key={name}><td>{name}</td><td>{format(rail.v, ' V', 3)}</td><td>{format(rail.a, ' A', 3)}</td></tr>)}</tbody></table>
    </details>}
  </div>;
}

function CategoryCard({ category, parts, data }) {
  return <article className="dm-card">
    <h3><MdDeveloperBoard aria-hidden="true" size={18} />{category}</h3>
    {parts.map(part => <PartBlock key={part.key} part={part} data={data} />)}
  </article>;
}

function HistoryChart({ samples, events, hours }) {
  const [metric, setMetric] = useState(CHART_METRICS[0][0]);
  const [, label, digits] = CHART_METRICS.find(([key]) => key === metric);
  const end = Date.now(), start = end - hours * 3600 * 1000;
  const points = samples.filter(s => typeof s[metric] === 'number').map(s => [new Date(s.at).getTime(), s[metric]]);
  const values = points.map(([, v]) => v);
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const x = t => ((t - start) / (end - start)) * 600;
  const y = v => 90 - ((v - min) / span) * 80;
  return <article className="dm-card dm-chart">
    <div className="dm-chart-top">
      <h3><MdTimeline aria-hidden="true" size={18} />History</h3>
      <div className="dm-filters dm-filters--small" role="group" aria-label="Chart metric">
        {CHART_METRICS.map(([key, name]) => <button key={key} type="button" aria-pressed={metric === key} onClick={() => setMetric(key)}>{name}</button>)}
      </div>
    </div>
    {points.length < 2 ? <p className="dm-muted">Not enough history for {label} yet. A sample is stored every 5 minutes.</p> : <>
      <svg viewBox="0 0 600 100" preserveAspectRatio="none" className="dm-chart-svg" role="img" aria-label={`${label} over the last ${hours} hours`}>
        {events.map(event => <line key={`${event.at}-${event.component}`} x1={x(new Date(event.at).getTime())} x2={x(new Date(event.at).getTime())} y1="0" y2="100"
          className={`dm-chart-event dm-chart-event--${tone(event.to)}`} />)}
        <polyline fill="none" className="dm-chart-line" points={points.map(([t, v]) => `${x(t)},${y(v)}`).join(' ')} />
      </svg>
      <div className="dm-chart-legend"><span>{label}: min {min.toFixed(digits)} · max {max.toFixed(digits)} · now {values[values.length - 1].toFixed(digits)}</span><span>Lines mark status changes</span></div>
    </>}
  </article>;
}

function EventList({ events }) {
  return <article className="dm-card">
    <h3><MdHistory aria-hidden="true" size={18} />Events</h3>
    {events.length === 0 ? <p className="dm-muted">No status changes in this period.</p> :
      <ol className="dm-events">{events.map(event => <li key={`${event.at}-${event.component}-${event.label}`}>
        <time dateTime={event.at}>{new Date(event.at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</time>
        <div>{event.to ? <StatusPill status={event.to}>{event.label}</StatusPill> : <strong>{event.label}</strong>}
          {event.detail && <p>{event.detail}</p>}</div>
      </li>)}</ol>}
  </article>;
}

export default function DeviceHealthDetail({ deviceId, refreshKey, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [hours, setHours] = useState(24);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchJson(`/admin/device-health/${deviceId}?hours=${hours}`)
      .then(result => { if (!cancelled) { setData(result); setError(''); } })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [deviceId, refreshKey, hours, version]);

  if (error) return <section className="dm-detail"><div className="dm-error" role="alert">{error}</div></section>;
  if (!data || data.device_id !== deviceId) return <section className="dm-detail"><p className="dm-muted">Loading device…</p></section>;

  const report = data.report || {};
  const system = report.system || {};
  const problems = Object.entries(data.components || {}).filter(([, item]) => ['critical', 'warning'].includes(item.status));

  return <section className="dm-detail" aria-label={`Health of ${data.public_id}`}>
    <header className="dm-detail-header">
      <div>
        <h2>{data.public_id}</h2>
        <p className="dm-muted">
          {[system.model, report.release?.id && `release ${report.release.id}`, system.uptime_s != null && `up ${duration(system.uptime_s)}`,
            `reported ${relativeTime(data.report_received_at)}`, data.agent_version && `agent v${data.agent_version}`].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="dm-detail-actions">
        {data.offline && <StatusPill status="offline">{data.overall === 'no_data' ? 'No agent' : 'Offline — showing last report'}</StatusPill>}
        {problems.map(([key, item]) => <StatusPill key={key} status={item.status}>{item.label}</StatusPill>)}
        <button type="button" className="dm-icon-button" onClick={onClose} aria-label="Close device details"><MdClose size={20} /></button>
      </div>
    </header>

    <InstalledHardware key={data.device_id} device={data} onSaved={() => setVersion(v => v + 1)} />

    {!data.report ? <p className="dm-muted">This device has not sent a health report yet. Install the health agent on it with <code>make install-health</code>.</p> : <>
      <div className="dm-cards">
        {groupByCategory(data.parts || []).map(([category, parts]) => <CategoryCard key={category} category={category} parts={parts} data={data} />)}
        {BOARD_CARDS.map(card => <BoardCard key={card.component} card={card} data={data} />)}
      </div>

      <div className="dm-filters" role="group" aria-label="History range">
        {RANGES.map(([value, name]) => <button key={value} type="button" aria-pressed={hours === value} onClick={() => setHours(value)}>{name}</button>)}
      </div>
      <div className="dm-history">
        <HistoryChart samples={data.history?.samples || []} events={(data.events || []).filter(e => e.to)} hours={data.history?.hours || hours} />
        <EventList events={data.events || []} />
      </div>

      {(report.usb || []).length > 0 && <article className="dm-card dm-usb">
        <h3><MdUsb aria-hidden="true" size={18} />USB devices</h3>
        <p className="dm-note">Current is the maximum each device declares, not a measurement. The Pi 5 cannot measure per-port current.</p>
        <table className="dm-mini"><thead><tr><th>Device</th><th>ID</th><th>Declared max</th></tr></thead>
          <tbody>{report.usb.map(item => <tr key={item.port}><td>{item.name || item.manufacturer || 'Unknown'}</td><td>{item.vendor}:{item.product_id}</td><td>{item.declared_max_ma != null ? `${item.declared_max_ma} mA` : '—'}</td></tr>)}</tbody></table>
      </article>}

      <details className="dm-details dm-raw"><summary>Raw report</summary><pre>{JSON.stringify(report, null, 2)}</pre></details>
    </>}
  </section>;
}
